import { useCallback, useState } from 'react';
import { BotaoPrimario, IconeOlho, classesEntrada } from '../../components/gps/Modal';

/* ============================================================================
   Campos das telas de autenticacao.

   POR QUE ESTE ARQUIVO EXISTE: login, recuperar senha, criar conta e verificar
   codigo tinham CADA UMA a sua propria folha de `!important` mirando classes
   internas do MUI, repetida quase igual quatro vezes (com divergencias: a de
   reset-password escondia `.MuiOutlinedInput-notchedOutline`, a de login nao).

   MUDANCA DE 18/08/2026 - DUAS, e as duas corrigem coisa que eu havia escrito
   errado aqui:

   1. AS CORES ERAM LITERAIS (`#DCE7F7`, `#0F172A`, `#1E88E5`) porque eu tinha
      decidido que as telas de auth ficariam fora do tema. Nao ha razao para isso:
      quem escolhe tema escuro quer o login escuro tambem. Agora tudo sai dos
      tokens, exatamente como no resto do sistema.

   2. O BOTAO E O PADRAO DO SISTEMA (`BotaoPrimario` de components/gps/Modal), nao
      mais um gradiente proprio com sombra azul. Botao de submit com aparencia
      exclusiva de uma tela e o tipo de coisa que envelhece sozinha: muda o
      primario do sistema e o login continua com o antigo, sem ninguem notar.

   A entrada tambem passou a usar `classesEntrada`, a mesma dos formularios - com
   uma diferenca justificada: aqui a altura e maior (h-11 em vez de h-9), porque
   no login ha tres campos numa tela vazia, e nao trinta numa tela cheia.

   ESCOLHAS DE 20/08/2026 (feitas na tela de escolha, com previa ao vivo):
     - halo no campo em foco;
     - aviso de Caps Lock no campo de senha;
     - botao que afunda 1% no clique e mostra tres pontos enquanto envia.
   Nada disso roda para quem tem `prefers-reduced-motion` - a guarda esta em
   theme/global.css e vale para o sistema inteiro.
   ============================================================================ */

/* Entrada de auth: `classesEntrada` do sistema, um pouco mais alta, com HALO NO
   FOCO (escolhido em 20/08/2026).

   `classesEntrada` ja troca a cor da borda no foco. O halo (`ring-2` numa versao
   diluida do primary) acrescenta o que a borda sozinha nao da: numa tela de tres
   campos, a borda de 1px mudando de cinza para azul-marinho e uma diferenca
   pequena, e quem chega pelo teclado precisa achar onde esta em um relance.

   `/20` e nao `/40`: o halo tem que dizer "e aqui", nao competir com o campo. E
   `transition-shadow` de 150ms para ele aparecer, e nao piscar. */
export const ENTRADA_AUTH = `${classesEntrada} h-11 text-[14px] transition-shadow focus:ring-2 focus:ring-primary/20`;

/* ----------------------------------------------------------------------------
   Icones.

   POR QUE SAO SVG E NAO EMOJI: os campos usavam ✉ e 🔒 como texto. Emoji e
   desenhado pela FONTE do sistema - no Windows sai colorido (o cadeado amarelo do
   print), no Mac sai outro desenho, no Linux as vezes sai o retangulo vazio. Um
   icone de campo tem que ser da cor do campo; ele nao e conteudo, e sinalizacao.

   `currentColor` em todos: assim o icone escurece e clareia com o tema junto do
   texto ao lado, sem nenhuma cor escrita aqui.
   ---------------------------------------------------------------------------- */

const TRACO = { stroke: 'currentColor', strokeWidth: 1.6, strokeLinecap: 'round', strokeLinejoin: 'round' };

export function IconeEmail(props) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden {...props}>
      <rect x="3" y="5" width="18" height="14" rx="2.5" {...TRACO} />
      <path d="M3.5 7.5l7.4 5.2a2 2 0 002.2 0l7.4-5.2" {...TRACO} />
    </svg>
  );
}

export function IconeCadeado(props) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden {...props}>
      <rect x="4.5" y="10.5" width="15" height="10" rx="2.5" {...TRACO} />
      <path d="M8.5 10.5V7.8a3.5 3.5 0 017 0v2.7" {...TRACO} />
      <circle cx="12" cy="15.5" r="1.4" fill="currentColor" />
    </svg>
  );
}

export function IconePessoa(props) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden {...props}>
      <circle cx="12" cy="8" r="3.5" {...TRACO} />
      <path d="M4.5 20c0-3.6 3.4-5.5 7.5-5.5s7.5 1.9 7.5 5.5" {...TRACO} />
    </svg>
  );
}

/**
 * Botao de mostrar/ocultar senha.
 *
 * ERA A PALAVRA "ver" / "ocultar" em texto. Tres problemas: ela ocupava largura
 * variavel (o campo precisava de `pr-[68px]` reservado para a maior das duas),
 * competia com o texto digitado logo ao lado, e ficava dentro do campo parecendo
 * parte do valor. O icone e o padrao que qualquer pessoa reconhece ali.
 *
 * `aria-label` e `aria-pressed` continuam: para o leitor de tela o botao segue
 * sendo "Mostrar senha", pressionado ou nao - o icone nao carrega nome nenhum.
 */
export function BotaoOlho({ mostrando, onAlternar }) {
  return (
    <button
      type="button"
      onClick={onAlternar}
      aria-label={mostrando ? 'Ocultar senha' : 'Mostrar senha'}
      aria-pressed={mostrando}
      title={mostrando ? 'Ocultar senha' : 'Mostrar senha'}
      className="absolute right-1.5 top-1/2 grid h-8 w-8 -translate-y-1/2 place-items-center rounded-md text-muted-foreground hover:bg-soft hover:text-ink"
    >
      <IconeOlho fechado={mostrando} />
    </button>
  );
}

export function CampoAuth({ id, rotulo, erro, children, dica }) {
  return (
    <div>
      <label htmlFor={id} className="mb-1 block text-[12px] font-semibold text-muted-foreground">
        {rotulo}
      </label>
      {children}
      {dica && !erro && <p className="mt-1 text-[11px] text-faint">{dica}</p>}
      {erro && <p className="mt-1 text-[12px] text-danger">{erro}</p>}
    </div>
  );
}

/**
 * Campo de senha com o olho de mostrar/ocultar.
 *
 * O botao tem `aria-label` e `aria-pressed` - nas versoes MUI era um IconButton
 * com um icone dentro e nenhum nome acessivel, entao o leitor de tela anunciava
 * apenas "botao".
 */
export function SenhaAuth({
  id,
  rotulo,
  erro,
  registro,
  autoComplete = 'new-password',
  placeholder = '••••••••',
  dica,
}) {
  const [mostrar, setMostrar] = useState(false);
  const [capsLigado, setCapsLigado] = useState(false);

  /* AVISO DE CAPS LOCK (escolhido em 20/08/2026).

     Por que vale a pena: senha e o unico campo do sistema em que a pessoa NAO VE
     o que digitou. Com Caps Lock ligado ela digita a senha certa, recebe "senha
     invalida" e nao tem como descobrir por que - e uma parte real dos pedidos de
     "resetar minha senha" no suporte e exatamente isto.

     `getModifierState('CapsLock')` le o estado REAL da tecla no sistema, e nao
     "se a tecla CapsLock foi pressionada agora". A diferenca importa: com o
     segundo, chegar no campo com o Caps ja ligado nao avisaria nada.

     Escutamos `keyup` alem de `keydown` porque a tecla que MUDA o estado precisa
     do evento de soltar para o novo valor aparecer. E o `onBlur` limpa: aviso
     preso num campo que ninguem esta usando e ruido.

     Nao ha como saber o estado sem interacao (o navegador nao expoe), entao o
     aviso aparece na primeira tecla - o que e cedo o suficiente. */
  const conferirCaps = useCallback((e) => {
    if (typeof e.getModifierState !== 'function') return;
    setCapsLigado(e.getModifierState('CapsLock'));
  }, []);

  return (
    <CampoAuth id={id} rotulo={rotulo} erro={erro} dica={dica}>
      <div className="relative">
        <IconeCadeado className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-faint" />
        <input
          id={id}
          type={mostrar ? 'text' : 'password'}
          autoComplete={autoComplete}
          placeholder={placeholder}
          className={`${ENTRADA_AUTH} pl-9 pr-10`}
          aria-invalid={Boolean(erro)}
          {...registro}
          // `registro` vem antes: os handlers do react-hook-form (onBlur/onChange)
          // precisam continuar valendo, e os nossos abaixo se somam a eles.
          onKeyDown={conferirCaps}
          onKeyUp={conferirCaps}
          onBlur={(e) => {
            setCapsLigado(false);
            registro?.onBlur?.(e);
          }}
        />
        <BotaoOlho mostrando={mostrar} onAlternar={() => setMostrar(!mostrar)} />
      </div>

      {/* `aria-live="polite"`: quem usa leitor de tela tambem precisa ser avisado,
          e sem isto o texto apareceria sem ser anunciado. "polite" para nao cortar
          o que o leitor estiver falando. */}
      {capsLigado && (
        <span className="mt-1 flex items-center gap-1.5 text-[11px] font-semibold text-warn" aria-live="polite">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden>
            <path
              d="M12 4l7 7h-4v5H9v-5H5l7-7zM8 19h8"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          Caps Lock está ligado
        </span>
      )}
    </CampoAuth>
  );
}

/** Campo de texto com icone a esquerda - e-mail, nome, o que tiver icone. */
export function EntradaAuth({ id, icone, erro, className = '', ...props }) {
  return (
    <div className="relative">
      {icone && (
        <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-faint">{icone}</span>
      )}
      <input
        id={id}
        aria-invalid={Boolean(erro)}
        className={`${ENTRADA_AUTH} ${icone ? 'pl-9' : ''} ${className}`}
        {...props}
      />
    </div>
  );
}

/**
 * Botao de submit das telas de auth: o `BotaoPrimario` do sistema, esticado e um
 * pouco mais alto. `w-full` porque num formulario de tres campos o alvo largo e
 * mais facil de acertar, inclusive no toque.
 */
export function BotaoAuth({ children, carregando, rotuloCarregando = 'Enviando...', ...props }) {
  return (
    <BotaoPrimario
      type="submit"
      disabled={carregando}
      {...props}
      /* RESPOSTA DO BOTAO (escolhida em 20/08/2026): `active:scale-[0.99]`.
         Um por cento e quase nada de propósito - o suficiente para o clique ter
         retorno fisico, longe do "botao de gelatina". So aqui, e nao no
         BotaoPrimario do sistema: numa lista com dez acoes, dez botoes elasticos
         viram distracao; numa tela com UM botao, ele e a acao. */
      className="h-11 w-full text-[14px] transition-transform active:scale-[0.99]"
    >
      {carregando ? (
        <span className="inline-flex items-center gap-2">
          {rotuloCarregando}
          {/* Tres pontos pulsando em vez de roda girando: o rotulo ja diz o que
              esta acontecendo, e os pontos so confirmam que nao travou. */}
          <span className="inline-flex items-center gap-1" aria-hidden>
            <i className="h-1 w-1 animate-pulsar rounded-full bg-current" />
            <i className="h-1 w-1 animate-pulsar rounded-full bg-current [animation-delay:150ms]" />
            <i className="h-1 w-1 animate-pulsar rounded-full bg-current [animation-delay:300ms]" />
          </span>
        </span>
      ) : (
        children
      )}
    </BotaoPrimario>
  );
}

export function LinkVoltar({ children = 'Voltar para o login', href, Componente }) {
  return (
    <div className="mt-5 text-center">
      <Componente
        href={href}
        className="inline-flex items-center gap-1 text-[12.5px] font-semibold text-muted-foreground hover:text-ink hover:underline"
      >
        <span aria-hidden>&larr;</span> {children}
      </Componente>
    </div>
  );
}

/* `SeloSeguranca` SAIU em 20/08/2026.

   Ele desenhava "Conexão segura com criptografia SSL/TLS" e era colado como
   `footer` em cada uma das quatro telas de auth. Com o rodape de confianca no
   AuthLayout - que traz o mesmo selo mais a versao e o aviso de ambiente, num
   lugar so - ele passou a DUPLICAR a linha na tela. Quatro chamadas apagadas
   junto. */
